import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link as RouterLink, Navigate } from 'react-router-dom';
import { Container, Typography, Box, Button } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { blogPosts } from '../data/blogPosts';

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function TldrCard({ tldr }) {
  return (
    <Box sx={{
      pl: 2.5,
      borderLeft: '1px solid rgba(255,255,255,0.1)',
    }}>
      <Typography sx={{
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: 'rgba(245,245,247,0.4)',
        fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
        mb: 2.5,
      }}>
        TL;DR
      </Typography>
      <Box component="ul" sx={{ pl: 0, m: 0, listStyle: 'none' }}>
        {tldr.map((point, i) => (
          <Box
            key={i}
            component="li"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.4,
              mb: i < tldr.length - 1 ? 2 : 0,
            }}
          >
            <Box sx={{
              width: 14,
              flexShrink: 0,
              mt: '0.62em',
              height: '1px',
              background: 'rgba(245,245,247,0.28)',
            }} />
            <Typography sx={{
              color: 'rgba(245,245,247,0.66)',
              fontSize: '0.84rem',
              lineHeight: 1.7,
            }}>
              {point}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TocSidebar({ headings, activeId, onJump }) {
  return (
    <Box component="nav">
      <Typography sx={{
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: 'rgba(245,245,247,0.4)',
        fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
        mb: 2.5,
        pl: 2,
      }}>
        Contents
      </Typography>
      <Box>
        {headings.map((h) => {
          const hid = `section-${h.index}`;
          const active = hid === activeId;
          return (
            <Box
              key={hid}
              component="button"
              onClick={() => onJump(hid)}
              sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: 'none',
                border: 0,
                borderLeft: '2px solid',
                borderLeftColor: active ? '#F5F5F7' : 'rgba(255,255,255,0.12)',
                cursor: 'pointer',
                px: 2,
                py: 1,
                color: active ? '#F5F5F7' : 'rgba(245,245,247,0.45)',
                fontFamily: 'inherit',
                fontSize: '0.84rem',
                lineHeight: 1.4,
                letterSpacing: '-0.005em',
                transition: 'color 0.2s ease, border-color 0.2s ease',
                '&:hover': { color: '#F5F5F7' },
              }}
            >
              {h.text}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// Render a single content block
function Block({ block, id }) {
  switch (block.type) {
    case 'paragraph':
      return (
        <Typography sx={{
          color: 'rgba(245,245,247,0.78)',
          fontSize: '1.12rem',
          lineHeight: 1.85,
          letterSpacing: '-0.003em',
          mb: 3,
        }}>
          {block.text}
        </Typography>
      );

    case 'heading':
      return (
        <Typography id={id} variant="h4" sx={{
          color: '#F5F5F7',
          fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, sans-serif',
          fontWeight: 600,
          fontSize: 'clamp(1.35rem, 3vw, 1.7rem)',
          letterSpacing: '-0.03em',
          mt: 6,
          mb: 2,
          scrollMarginTop: '90px',
        }}>
          {block.text}
        </Typography>
      );

    case 'subheading':
      return (
        <Typography variant="h5" sx={{
          color: 'rgba(245,245,247,0.92)',
          fontWeight: 600,
          fontSize: '1.18rem',
          letterSpacing: '-0.015em',
          mt: 4,
          mb: 1.4,
        }}>
          {block.text}
        </Typography>
      );

    case 'list':
      return (
        <Box component="ul" sx={{ pl: 0, mb: 3, listStyle: 'none' }}>
          {block.items.map((item, i) => (
            <Box
              key={i}
              component="li"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.6,
                mb: 1.4,
                color: 'rgba(245,245,247,0.78)',
                fontSize: '1.08rem',
                lineHeight: 1.75,
              }}
            >
              <Box sx={{
                width: 16,
                height: '1px',
                background: 'rgba(245,245,247,0.3)',
                flexShrink: 0,
                mt: '0.85em',
              }} />
              {item}
            </Box>
          ))}
        </Box>
      );

    case 'code':
      return (
        <Box
          component="pre"
          sx={{
            background: 'rgba(255,255,255,0.025)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px',
            p: 2.5,
            my: 3.5,
            overflowX: 'auto',
            fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
            fontSize: '0.86rem',
            color: 'rgba(245,245,247,0.82)',
            lineHeight: 1.75,
          }}
        >
          <code>{block.text}</code>
        </Box>
      );

    case 'quote':
      return (
        <Box sx={{
          borderLeft: '2px solid rgba(255,255,255,0.18)',
          pl: 3.5,
          my: 4,
        }}>
          <Typography sx={{
            color: 'rgba(245,245,247,0.92)',
            fontSize: 'clamp(1.2rem, 2.6vw, 1.45rem)',
            fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            lineHeight: 1.5,
            mb: block.author ? 1.5 : 0,
          }}>
            {block.text}
          </Typography>
          {block.author && (
            <Typography sx={{
              color: 'rgba(245,245,247,0.45)',
              fontSize: '0.82rem',
              letterSpacing: '0.02em',
              fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
            }}>
              — {block.author}
            </Typography>
          )}
        </Box>
      );

    case 'image':
      return (
        <Box sx={{ my: 4 }}>
          <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Box
              component="img"
              src={block.src}
              alt={block.alt || ''}
              sx={{ maxWidth: '100%', maxHeight: 360, display: 'block', margin: '0 auto' }}
            />
          </Box>
          {block.alt && (
            <Typography sx={{
              fontSize: '0.8rem',
              color: 'rgba(245,245,247,0.4)',
              textAlign: 'center',
              mt: 1.2,
            }}>
              {block.alt}
            </Typography>
          )}
        </Box>
      );

    case 'divider':
      return (
        <Typography sx={{
          textAlign: 'center',
          color: 'rgba(245,245,247,0.28)',
          letterSpacing: '0.6em',
          fontSize: '0.9rem',
          my: 6,
          userSelect: 'none',
        }}>
          * * *
        </Typography>
      );

    default:
      return null;
  }
}

export default function BlogPost() {
  const { id } = useParams();
  const post    = blogPosts.find((p) => p.id === id);

  const headings = (post?.content || [])
    .map((block, index) => ({ ...block, index }))
    .filter((block) => block.type === 'heading');
  const hasToc = headings.length >= 2;

  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (!hasToc) return undefined;
    const ids = headings.map((h) => `section-${h.index}`);
    setActiveId(ids[0]);
    const els = ids.map((sid) => document.getElementById(sid)).filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const top = visible.reduce((a, b) =>
          a.boundingClientRect.top < b.boundingClientRect.top ? a : b
        );
        setActiveId(top.target.id);
      },
      { rootMargin: '-88px 0px -65% 0px', threshold: 0 }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id, hasToc]);

  if (!post) return <Navigate to="/blog" replace />;

  const handleJump = (sid) => {
    const el = document.getElementById(sid);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const hasTldr = Array.isArray(post.tldr) && post.tldr.length > 0;

  const siteUrl = 'https://beta.yarmoshik.com';
  const postUrl = `${siteUrl}/blog/${post.id}`;
  const imageUrl = post.coverImage ? `${siteUrl}${post.coverImage}` : `${siteUrl}/logo512.png`;

  return (
    <Container maxWidth={hasToc ? 'lg' : 'md'} sx={{ mt: { xs: 8, md: 12 }, mb: 10 }}>
      <Helmet>
        <title>{post.title} - Michael Yarmoshik</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={postUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={imageUrl} />
      </Helmet>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Back link */}
        <Box
          component={RouterLink}
          to="/blog"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.8,
            color: 'rgba(245,245,247,0.45)',
            textDecoration: 'none',
            fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
            fontSize: '0.78rem',
            mb: 6,
            transition: 'color 0.2s ease',
            '&:hover': { color: '#F5F5F7' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: '0.9rem' }} />
          Writing
        </Box>

        {/* Two-column layout */}
        <Box sx={{ display: 'flex', gap: { xs: 0, lg: 5 }, alignItems: 'flex-start' }}>

          {/* Main content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* Meta */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
              <Typography sx={{
                fontSize: '0.78rem',
                color: 'rgba(245,245,247,0.4)',
                fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
              }}>
                {formatDate(post.date)}
              </Typography>
              {post.tags.map((tag) => (
                <Box key={tag} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(245,245,247,0.25)' }} />
                  <Typography sx={{
                    fontSize: '0.78rem',
                    color: 'rgba(245,245,247,0.4)',
                    fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
                  }}>
                    {tag}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Title */}
            <Typography variant="h2" sx={{
              color: '#F5F5F7',
              fontWeight: 700,
              mb: 2.5,
              lineHeight: 1.12,
              letterSpacing: '-0.035em',
              fontSize: 'clamp(2rem, 5.5vw, 3rem)',
            }}>
              {post.title}
            </Typography>

            {/* Excerpt / dek */}
            <Typography sx={{
              color: 'rgba(245,245,247,0.55)',
              fontSize: 'clamp(1.15rem, 2.4vw, 1.35rem)',
              lineHeight: 1.55,
              letterSpacing: '-0.01em',
              mb: 5,
            }}>
              {post.excerpt}
            </Typography>

            {/* Cover image */}
            {post.coverImage && (
              <Box sx={{
                height: { xs: 200, md: 340 },
                borderRadius: '12px',
                overflow: 'hidden',
                mb: 5,
                border: '1px solid rgba(255,255,255,0.08)',
                backgroundImage: `url(${post.coverImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }} />
            )}

            {/* TLDR card — mobile only (above content) */}
            {hasTldr && (
              <Box sx={{ display: { xs: 'block', lg: 'none' }, mb: 4 }}>
                <TldrCard tldr={post.tldr} />
              </Box>
            )}

            {/* Divider rule */}
            <Box sx={{
              height: '1px',
              background: 'rgba(255,255,255,0.1)',
              mb: 5,
            }} />

            {/* Content blocks */}
            <Box sx={{ maxWidth: 720 }}>
              {post.content.map((block, i) => (
                <Block key={i} block={block} id={block.type === 'heading' ? `section-${i}` : undefined} />
              ))}
            </Box>

            {/* Footer nav */}
            <Box sx={{
              mt: 8,
              pt: 4,
              borderTop: '1px solid rgba(255,255,255,0.07)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <Button
                component={RouterLink}
                to="/blog"
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: 'rgba(245,245,247,0.7)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  borderRadius: '10px',
                  px: 2.5,
                  textTransform: 'none',
                  '&:hover': {
                    color: '#F5F5F7',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.28)',
                  },
                }}
              >
                All posts
              </Button>
            </Box>
          </Box>

          {/* Section tracker sidebar — desktop only */}
          {hasToc && (
            <Box sx={{
              display: { xs: 'none', lg: 'block' },
              width: 240,
              flexShrink: 0,
              position: 'sticky',
              top: 96,
              alignSelf: 'flex-start',
            }}>
              <TocSidebar headings={headings} activeId={activeId} onJump={handleJump} />
            </Box>
          )}

        </Box>
      </motion.div>
    </Container>
  );
}
